import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  useTheme,
  alpha,
  Chip,
  Card,
  CardMedia
} from '@mui/material';
import {
  Science as ScienceIcon,
  Assignment as AssignmentIcon,
  Biotech as BiotechIcon,
  FactCheck as FactCheckIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  LocalOffer as LocalOfferIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  ViewModule as ViewModuleIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  Verified as VerifiedIcon,
  Storage as StorageIcon,
  Inventory as InventoryIcon,
  Schedule as ScheduleIcon,
  Approval as ApprovalIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Category as CategoryIcon,
  TrackChanges as TrackChangesIcon,
  Transform as TransformIcon
} from '@mui/icons-material';
import Navigation from '../../Navigation';
import Footer from '../../Footer';

// Import line production images
import lineOverviewImage from '../assests/line-production-page/line-overview.png';
import lineItemsImage from '../assests/line-production-page/line-items.png';
import lineCostsImage from '../assests/line-production-page/line-costs.png';
import lineLogImage from '../assests/line-production-page/line-log.png';

const LineProductionLandingPage = ({ onNavigateToAuth }) => {
  const theme = useTheme();

  // Use a lighter orange color scheme
  const primaryColor = '#F39C12'; // Lighter orange
  const secondaryColor = '#E67E22'; // Original orange for accents

  // Hero Section Component
  const HeroSection = () => (
    <Box
      sx={{
        pt: { xs: 10, md: 20 },
        pb: { xs: 8, md: 12 },
        mt: { xs: 0, md: -10 },
        backgroundColor: '#ffffff',
        textAlign: 'center',
        minHeight: { xs: '80vh', md: '85vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Design Elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        opacity: 0.1
      }}>
        <Box sx={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: { xs: 150, md: 200 },
          height: { xs: 150, md: 200 },
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${primaryColor}, #F1C40F)`,
          animation: 'float 7s ease-in-out infinite'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '30%',
          left: '15%',
          width: { xs: 120, md: 150 },
          height: { xs: 120, md: 150 },
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${secondaryColor}, #E67E22)`,
          animation: 'float 9s ease-in-out infinite reverse'
        }} />
        <Box sx={{
          position: 'absolute',
          top: '60%',
          right: '25%',
          width: { xs: 100, md: 120 },
          height: { xs: 100, md: 120 },
          borderRadius: '50%',
          background: `linear-gradient(135deg, #3498DB, #2980B9)`,
          animation: 'float 5s ease-in-out infinite'
        }} />
        <style>
          {`
            @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-25px) rotate(5deg); }
            }
          `}
        </style>
      </Box>

      <Box sx={{ maxWidth: '850px', mx: 'auto', px: { xs: 2, md: 3 }, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '1.75rem', md: '4.2rem' },
            fontWeight: 400,
            color: '#2d3748',
            mb: { xs: 2.5, md: 4 },
            lineHeight: 1.1
          }}
        >
          Optimize Your Line Production with{' '}
          <Box
            component="span"
            sx={{
              backgroundColor: primaryColor,
              color: '#ffffff',
              px: { xs: 2, md: 3 },
              py: { xs: 0.75, md: 1 },
              borderRadius: { xs: 2, md: 3 },
              display: 'inline-block',
              fontWeight: 500
            }}
          >
            Smart Manufacturing
          </Box>
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: '1rem', md: '1.5rem' },
            fontWeight: 400,
            color: '#718096',
            mb: { xs: 3, md: 5 },
            fontStyle: 'italic',
            px: { xs: 1, md: 0 }
          }}
        >
          Complete production line management, product templates, and seamless integration with batch production for end-to-end manufacturing control!
        </Typography>
        <Stack direction="row" spacing={{ xs: 1.5, md: 2 }} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={onNavigateToAuth}
            sx={{
              backgroundColor: primaryColor,
              color: '#ffffff',
              fontWeight: 500,
              px: { xs: 3, md: 5 },
              py: { xs: 1.5, md: 2 },
              textTransform: 'none',
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              borderRadius: { xs: 2, md: 3 },
              '&:hover': {
                backgroundColor: '#E67E22'
              }
            }}
          >
            Start Line Production
          </Button>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: '#9ca3af',
            mt: { xs: 2.5, md: 4 },
            fontSize: { xs: '0.8rem', md: '0.9rem' }
          }}
        >
          Professional manufacturing tools - Begin managing your production lines immediately
        </Typography>
      </Box>
    </Box>
  );

  // Scrolling Tags Component
  const ScrollingTags = () => (
    <Box sx={{
      width: '100%',
      py: { xs: 2, md: 3 },
      mt: { xs: -8, md: -15 },
      bgcolor: alpha(theme.palette.grey[50], 0.8),
      overflow: 'hidden',
      position: 'relative'
    }}>
      <Box sx={{
        display: 'flex',
        gap: { xs: 2, md: 3 },
        animation: { xs: 'scroll 40s linear infinite', md: 'scroll 65s linear infinite' },
        width: 'max-content',
        '@keyframes scroll': {
          '0%': {
            transform: 'translateX(0)'
          },
          '100%': {
            transform: 'translateX(-50%)'
          }
        }
      }}>
        {[
          'Line Production',
          'Product Templates',
          'Batch Integration',
          'Material Tracking',
          'Production Lines',
          'Inventory Transfer',
          'Lot Tracking',
          'Production Logging',
          'Quality Control',
          'Workflow Management',
          'Resource Planning',
          'Cost Analysis',
          'Production Templates',
          'Material Consumption',
          'Line Scheduling',
          'Product Assembly',
          'Batch Linking',
          'Inventory Management',
          'Production Monitoring',
          'Template Management',
          'Line Production',
          'Product Templates',
          'Batch Integration',
          'Material Tracking',
          'Production Lines',
          'Inventory Transfer',
          'Lot Tracking',
          'Production Logging',
          'Quality Control',
          'Workflow Management',
          'Resource Planning',
          'Cost Analysis',
          'Production Templates',
          'Material Consumption',
          'Line Scheduling',
          'Product Assembly',
          'Batch Linking',
          'Inventory Management',
          'Production Monitoring',
          'Template Management'
        ].map((tag, index) => (
          <Chip
            key={index}
            label={tag}
            sx={{
              fontSize: { xs: '0.85rem', md: '1rem' },
              fontWeight: 500,
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 },
              height: 'auto',
              borderRadius: { xs: 2, md: 3 },
              bgcolor: 'white',
              color: 'text.primary',
              border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.grey[900], 0.08)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              whiteSpace: 'nowrap',
              cursor: 'default',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 20px ${alpha(primaryColor, 0.15)}`,
                bgcolor: alpha(primaryColor, 0.05),
                borderColor: alpha(primaryColor, 0.3),
                color: primaryColor
              }
            }}
          />
        ))}
      </Box>

      {/* Gradient overlays for smooth fade effect */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to right, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: { xs: 60, md: 100 },
        height: '100%',
        background: `linear-gradient(to left, ${alpha(theme.palette.grey[50], 0.8)}, transparent)`,
        pointerEvents: 'none',
        zIndex: 1
      }} />
    </Box>
  );

  // Features Overview Component - Zigzag Layout
  const FeaturesOverview = () => {
    const mainFeatures = [
      {
        icon: <SettingsIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Complete Line Production Management',
        description: 'End-to-end production line control with product templates, material consumption tracking, and seamless integration with batch production for complete manufacturing oversight.',
        color: primaryColor,
        items: ['Production lines', 'Product templates', 'Material consumption', 'Batch integration']
      },
      {
        icon: <TransformIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Product Template System',
        description: 'Create comprehensive product templates that define exactly what materials and processes are needed for each production run, ensuring consistency and accuracy.',
        color: secondaryColor,
        items: ['Template creation', 'Material requirements', 'Process definition', 'Quality standards']
      },
      {
        icon: <TrackChangesIcon sx={{ fontSize: 60, color: '#ffffff' }} />,
        title: 'Material & Inventory Integration',
        description: 'Automatic material consumption tracking with lot/batch traceability, seamless inventory transfers, and complete supply chain visibility throughout production.',
        color: '#3498DB',
        items: ['Material tracking', 'Lot traceability', 'Inventory transfer', 'Supply chain visibility']
      }
    ];

    return (
      <Box sx={{ py: 8, mt: 20 }}>
        <Box sx={{ px: { xs: 2, md: 0 } }}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 8,
              fontSize: { xs: '1.75rem', md: '3.2rem' },
              fontWeight: 300,
              color: '#1e293b',
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Everything You Need for{' '}
            <Box component="span" sx={{ fontWeight: 500, color: primaryColor }}>Manufacturing Excellence</Box>
          </Typography>
        </Box>

        {mainFeatures.map((feature, index) => (
          <Box
            key={index}
            sx={{
              py: 6,
              backgroundColor: index % 2 === 0 ? 'transparent' : alpha('#f8fafc', 0.7)
            }}
          >
            <Box sx={{
              maxWidth: '1300px',
              mx: 'auto',
              px: { xs: 2, md: 4 },
              display: 'flex',
              flexDirection: { xs: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' },
              gap: { xs: 3, md: 6 },
              alignItems: 'center'
            }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{
                  p: { xs: 3, md: 6 },
                  backgroundColor: feature.color,
                  borderRadius: { xs: 3, md: 6 },
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  boxShadow: `0 20px 60px ${alpha(feature.color, 0.3)}`
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: { xs: 80, md: 120 },
                    height: { xs: 80, md: 120 },
                    borderRadius: '50%',
                    backgroundColor: alpha('#ffffff', 0.1)
                  }} />
                  <Box sx={{ mb: { xs: 2, md: 3 } }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 500, mb: { xs: 2, md: 3 }, fontSize: { xs: '1.3rem', md: '1.8rem' } }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', md: '1.1rem' }, lineHeight: 1.6, mb: { xs: 2.5, md: 4 }, opacity: 0.9 }}>
                    {feature.description}
                  </Typography>
                  <Stack spacing={{ xs: 0.75, md: 1 }}>
                    {feature.items.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          width: { xs: 6, md: 8 },
                          height: { xs: 6, md: 8 },
                          borderRadius: '50%',
                          backgroundColor: '#ffffff',
                          mr: { xs: 1.5, md: 2 }
                        }} />
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>

              <Box sx={{ flex: 1, width: '100%' }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 2, md: 3 }
                }}>
                  {/* Unique feature highlights for each section */}
                  {index === 0 && (
                    <>
                      <Box sx={{
                        p: { xs: 3, md: 4 },
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <ViewModuleIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Production Lines
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Manage multiple production lines with real-time monitoring and control
                        </Typography>
                      </Box>

                      <Box sx={{
                        p: { xs: 3, md: 4 },
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <CategoryIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Product Templates
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Define standard processes and material requirements for consistent production
                        </Typography>
                      </Box>
                    </>
                  )}

                  {index === 1 && (
                    <>
                      <Box sx={{
                        p: { xs: 3, md: 4 },
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <AssignmentIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Template Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Create and manage product templates with detailed material and process requirements
                        </Typography>
                      </Box>

                      <Box sx={{
                        p: { xs: 3, md: 4 },
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <AnalyticsIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Process Definition
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Define step-by-step production processes and quality control checkpoints
                        </Typography>
                      </Box>
                    </>
                  )}

                  {index === 2 && (
                    <>
                      <Box sx={{
                        p: { xs: 3, md: 4 },
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <InventoryIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Material Integration
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Automatic material consumption tracking with lot and batch traceability
                        </Typography>
                      </Box>

                      <Box sx={{
                        p: { xs: 3, md: 4 },
                        backgroundColor: '#ffffff',
                        borderRadius: { xs: 3, md: 4 },
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <ApprovalIcon sx={{ fontSize: { xs: 32, md: 40 }, color: feature.color, mb: { xs: 1.5, md: 2 } }} />
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                          Inventory Transfer
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Seamless transfer of finished products to inventory with complete traceability
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Image Showcase Component - Large Image Focus
  const ImageShowcase = () => (
    <Box sx={{ py: { xs: 6, md: 12 }, mt: { xs: -6, md: -10 } }}>
      {/* First Image Section - Line Overview */}
      <Box sx={{ mb: { xs: 8, md: 16 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip
              icon={<ViewModuleIcon />}
              label="Line Overview"
              variant="outlined"
              sx={{
                borderColor: primaryColor,
                color: primaryColor,
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: primaryColor
                }
              }}
            />
          </Stack>

          <Typography variant="h2" sx={{
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Complete{' '}
            <Box component="span" sx={{ color: primaryColor }}>Line Management</Box>
          </Typography>

          <Typography variant="h6" sx={{
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Professional production line platform with comprehensive tracking and management tools
          </Typography>
        </Box>

        {/* Large Image Display */}
        <Box sx={{
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <img
              src={lineOverviewImage}
              alt="Line Production Overview"
              style={{
                width: '100%',
                maxWidth: '1400px',
                height: 'auto',
                borderRadius: '8px',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        {/* Feature Grid Below Image */}
        <Box sx={{
          maxWidth: '1200px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            {
              icon: ViewModuleIcon,
              title: 'Production Dashboard',
              desc: 'Comprehensive overview of all your production line activities and current status'
            },
            {
              icon: SettingsIcon,
              title: 'Line Configuration',
              desc: 'Configure and manage production lines with custom settings and parameters'
            },
            {
              icon: CategoryIcon,
              title: 'Template Integration',
              desc: 'Link product templates to production lines for consistent manufacturing processes'
            }
          ].map((item, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Box sx={{
                textAlign: 'center',
                p: { xs: 3, md: 4 },
                backgroundColor: '#ffffff',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                height: '100%',
                '&:hover': {
                  boxShadow: `0 8px 40px ${alpha(primaryColor, 0.12)}`,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{
                  width: { xs: 56, md: 64 },
                  height: { xs: 56, md: 64 },
                  borderRadius: '50%',
                  backgroundColor: alpha(primaryColor, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <item.icon sx={{ fontSize: { xs: 28, md: 32 }, color: primaryColor }} />
                </Box>
                <Typography variant="h6" sx={{
                  fontWeight: 500,
                  color: '#1e293b',
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontSize: { xs: '0.95rem', md: '1rem' }
                }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Line Items Section */}
      <Box sx={{ backgroundColor: alpha('#f8fafc', 0.6), py: { xs: 6, md: 12 }, mt: { xs: -8, md: -15 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip
              icon={<AssignmentIcon />}
              label="Production Items"
              variant="outlined"
              sx={{
                borderColor: secondaryColor,
                color: secondaryColor,
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: secondaryColor
                }
              }}
            />
          </Stack>

          <Typography variant="h2" sx={{
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Production Item{' '}
            <Box component="span" sx={{ color: secondaryColor }}>Management</Box>
          </Typography>

          <Typography variant="h6" sx={{
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Track production items, monitor progress, and manage quality control throughout the production process
          </Typography>
        </Box>

        {/* Line Items Image Display */}
        <Box sx={{
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 },
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <img
              src={lineItemsImage}
              alt="Production Line Items and Tracking"
              style={{
                width: '100%',
                maxWidth: '1200px',
                height: 'auto',
                borderRadius: '8px',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        {/* Feature List Below Images */}
        <Box sx={{
          maxWidth: '1000px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 4, md: 6 },
          alignItems: 'stretch'
        }}>
          {[
            {
              icon: AssignmentIcon,
              title: 'Item Tracking',
              desc: 'Monitor individual production items with real-time progress tracking and status updates'
            },
            {
              icon: CheckCircleIcon,
              title: 'Quality Control',
              desc: 'Implement quality checkpoints and verification processes for each production item'
            },
            {
              icon: TimelineIcon,
              title: 'Progress Monitoring',
              desc: 'Track production progress with detailed timelines and completion status for each item'
            }
          ].map((item, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  width: { xs: 64, md: 72 },
                  height: { xs: 64, md: 72 },
                  borderRadius: '50%',
                  backgroundColor: alpha(secondaryColor, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 },
                  border: `2px solid ${alpha(secondaryColor, 0.2)}`
                }}>
                  <item.icon sx={{ fontSize: { xs: 32, md: 36 }, color: secondaryColor }} />
                </Box>
                <Typography variant="h6" sx={{
                  fontWeight: 500,
                  color: '#1e293b',
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1.15rem', md: '1.3rem' }
                }}>
                  {item.title}
                </Typography>
                <Typography variant="body1" sx={{
                  color: '#64748b',
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', md: '1.1rem' }
                }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Cost Analysis Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, mt: { xs: -8, md: -15 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip
              icon={<AssessmentIcon />}
              label="Cost Analysis"
              variant="outlined"
              sx={{
                borderColor: '#27AE60',
                color: '#27AE60',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#27AE60'
                }
              }}
            />
          </Stack>

          <Typography variant="h2" sx={{
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Comprehensive{' '}
            <Box component="span" sx={{ color: '#27AE60' }}>Cost Tracking</Box>
          </Typography>

          <Typography variant="h6" sx={{
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Advanced cost analysis with detailed breakdowns and profitability calculations
          </Typography>
        </Box>

        {/* Cost Image Display */}
        <Box sx={{
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <img
              src={lineCostsImage}
              alt="Line Production Cost Analysis"
              style={{
                width: '100%',
                maxWidth: '1400px',
                height: 'auto',
                borderRadius: '8px',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        {/* Integration Features Section */}
        <Box sx={{ backgroundColor: alpha('#27AE60', 0.05), py: { xs: 4, md: 8 }, borderRadius: { xs: 3, md: 4 }, mx: { xs: 2, md: 4 } }}>
          <Typography variant="h3" sx={{
            textAlign: 'center',
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '1.5rem', md: '2.8rem' },
            px: { xs: 2, md: 0 }
          }}>
            Seamless Integration with Batch Production
          </Typography>

          <Box sx={{
            maxWidth: '1200px',
            mx: 'auto',
            px: { xs: 2, md: 4 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 4, md: 6 },
            alignItems: 'stretch'
          }}>
            {[
              {
                icon: StorageIcon,
                title: 'Batch-Linked Production',
                desc: 'Link line production with batch production to have full information about complete product lifecycle from raw materials to finished goods.',
                color: '#E67E22'
              },
              {
                icon: InventoryIcon,
                title: 'Product Templates',
                desc: 'Create product templates that tell production lines exactly what materials will be consumed and how products will be assembled.',
                color: primaryColor
              },
              {
                icon: TrackChangesIcon,
                title: 'Material & Product Tracking',
                desc: 'Transfer finished products into inventory with complete lot/batch tracking, and track lot/batch of all materials being used throughout production.',
                color: '#3498DB'
              }
            ].map((item, idx) => (
              <Box key={idx} sx={{ flex: 1, width: '100%' }}>
                <Box sx={{
                  textAlign: 'center',
                  p: { xs: 4, md: 6 },
                  backgroundColor: '#ffffff',
                  borderRadius: { xs: 3, md: 4 },
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  height: '100%',
                  '&:hover': {
                    boxShadow: `0 12px 40px ${alpha(item.color, 0.15)}`,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}>
                  <Box sx={{
                    width: { xs: 64, md: 80 },
                    height: { xs: 64, md: 80 },
                    borderRadius: '50%',
                    backgroundColor: alpha(item.color, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: { xs: 3, md: 4 },
                    border: `3px solid ${alpha(item.color, 0.2)}`
                  }}>
                    <item.icon sx={{ fontSize: { xs: 32, md: 40 }, color: item.color }} />
                  </Box>
                  <Typography variant="h5" sx={{
                    fontWeight: 500,
                    color: '#1e293b',
                    mb: { xs: 2, md: 3 },
                    fontSize: { xs: '1.2rem', md: '1.4rem' }
                  }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" sx={{
                    color: '#64748b',
                    lineHeight: 1.7,
                    fontSize: { xs: '1rem', md: '1.1rem' }
                  }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Line Log Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, mt: { xs: -8, md: -15 } }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Stack direction="row" justifyContent="center" sx={{ mb: { xs: 2, md: 3 } }}>
            <Chip
              icon={<TimelineIcon />}
              label="Production Logging"
              variant="outlined"
              sx={{
                borderColor: '#E74C3C',
                color: '#E74C3C',
                fontWeight: 500,
                px: { xs: 2, md: 3 },
                py: { xs: 0.75, md: 1 },
                fontSize: { xs: '0.9rem', md: '1rem' },
                '& .MuiChip-icon': {
                  color: '#E74C3C'
                }
              }}
            />
          </Stack>

          <Typography variant="h2" sx={{
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 2.5, md: 4 },
            fontSize: { xs: '1.75rem', md: '3.5rem' },
            maxWidth: '800px',
            mx: 'auto',
            px: { xs: 1, md: 0 }
          }}>
            Complete{' '}
            <Box component="span" sx={{ color: '#E74C3C' }}>Production Logging</Box>
          </Typography>

          <Typography variant="h6" sx={{
            color: '#64748b',
            fontSize: { xs: '1rem', md: '1.3rem' },
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6,
            px: { xs: 2, md: 0 }
          }}>
            Comprehensive production logging with workflow management and approval processes
          </Typography>
        </Box>

        {/* Line Log Image Display */}
        <Box sx={{
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          mb: { xs: 4, md: 8 }
        }}>
          <Box sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            <img
              src={lineLogImage}
              alt="Line Production Logging and Workflow"
              style={{
                width: '100%',
                maxWidth: '1400px',
                height: 'auto',
                borderRadius: '8px',
                display: 'block'
              }}
            />
          </Box>
        </Box>

        {/* Production Workflow Features */}
        <Box sx={{ backgroundColor: alpha('#E74C3C', 0.05), py: { xs: 4, md: 8 }, borderRadius: { xs: 3, md: 4 }, mx: { xs: 2, md: 4 } }}>
          <Typography variant="h3" sx={{
            textAlign: 'center',
            fontWeight: 500,
            color: '#1e293b',
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '1.5rem', md: '2.8rem' },
            px: { xs: 2, md: 0 }
          }}>
            Streamlined Production Workflow Management
          </Typography>

          <Box sx={{
            maxWidth: '1200px',
            mx: 'auto',
            px: { xs: 2, md: 4 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 4, md: 6 },
            alignItems: 'stretch'
          }}>
            {[
              {
                icon: ApprovalIcon,
                title: 'Production Request & Approval',
                desc: 'Submit production requests and manage approval workflows with automated notifications and status tracking.',
                color: '#E74C3C'
              },
              {
                icon: ScheduleIcon,
                title: 'Line Scheduling',
                desc: 'Schedule production runs on specific lines with resource allocation and timeline management.',
                color: '#3498DB'
              },
              {
                icon: TimelineIcon,
                title: 'Complete Process Logging',
                desc: 'Log the entire production process from start to finish with detailed tracking of materials, processes, and quality checks.',
                color: secondaryColor
              }
            ].map((item, idx) => (
              <Box key={idx} sx={{ flex: 1, width: '100%' }}>
                <Box sx={{
                  textAlign: 'center',
                  p: { xs: 4, md: 6 },
                  backgroundColor: '#ffffff',
                  borderRadius: { xs: 3, md: 4 },
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  height: '100%',
                  '&:hover': {
                    boxShadow: `0 12px 40px ${alpha(item.color, 0.15)}`,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s ease'
                  }
                }}>
                  <Box sx={{
                    width: { xs: 64, md: 80 },
                    height: { xs: 64, md: 80 },
                    borderRadius: '50%',
                    backgroundColor: alpha(item.color, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: { xs: 3, md: 4 },
                    border: `3px solid ${alpha(item.color, 0.2)}`
                  }}>
                    <item.icon sx={{ fontSize: { xs: 32, md: 40 }, color: item.color }} />
                  </Box>
                  <Typography variant="h5" sx={{
                    fontWeight: 500,
                    color: '#1e293b',
                    mb: { xs: 2, md: 3 },
                    fontSize: { xs: '1.2rem', md: '1.4rem' }
                  }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" sx={{
                    color: '#64748b',
                    lineHeight: 1.7,
                    fontSize: { xs: '1rem', md: '1.1rem' }
                  }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Floating Stats Cards */}
      <Box sx={{ position: 'relative', mt: { xs: 4, md: -8 } }}>
        <Box sx={{
          maxWidth: '1000px',
          mx: 'auto',
          mt: { xs: 4, md: 10 },
          px: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 },
          alignItems: 'stretch'
        }}>
          {[
            { icon: SettingsIcon, number: '50+', label: 'Production Lines', color: primaryColor },
            { icon: CheckCircleIcon, number: '95%', label: 'Template Compliance', color: secondaryColor },
            { icon: TrackChangesIcon, number: '100%', label: 'Material Traceability', color: '#3498DB' }
          ].map((stat, idx) => (
            <Box key={idx} sx={{ flex: 1, width: '100%' }}>
              <Card sx={{
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                backgroundColor: '#ffffff',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                borderRadius: { xs: 3, md: 4 },
                border: '1px solid #e2e8f0',
                transform: { xs: 'translateY(0)', md: 'translateY(-20px)' },
                '&:hover': {
                  transform: { xs: 'translateY(0)', md: 'translateY(-25px)' },
                  boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease'
                }
              }}>
                <Box sx={{
                  width: { xs: 64, md: 80 },
                  height: { xs: 64, md: 80 },
                  borderRadius: '50%',
                  backgroundColor: alpha(stat.color, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 2, md: 3 }
                }}>
                  <stat.icon sx={{ fontSize: { xs: 32, md: 40 }, color: stat.color }} />
                </Box>
                <Typography variant="h2" sx={{
                  fontWeight: 500,
                  color: stat.color,
                  mb: 1,
                  fontSize: { xs: '2.5rem', md: '3rem' }
                }}>
                  {stat.number}
                </Typography>
                <Typography variant="h6" sx={{
                  fontWeight: 500,
                  color: '#374151',
                  fontSize: { xs: '1.05rem', md: '1.2rem' }
                }}>
                  {stat.label}
                </Typography>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // CTA Section Component
  const CTASection = () => (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: primaryColor,
        color: '#fff',
        textAlign: 'center'
      }}
    >
      <Box sx={{ maxWidth: '600px', mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            fontWeight: 400,
            mb: { xs: 2, md: 3 },
            color: '#ffffff'
          }}
        >
          Ready to optimize your line production?
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: '1rem', md: '1.1rem' },
            fontWeight: 400,
            mb: { xs: 3, md: 4 },
            color: '#ffffff'
          }}
        >
          Join manufacturing teams that have optimized their production processes and improved quality control with our comprehensive line management platform.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onNavigateToAuth}
          sx={{
            backgroundColor: '#ffffff',
            color: primaryColor,
            fontWeight: 500,
            px: { xs: 4, md: 5 },
            py: { xs: 1.5, md: 2 },
            textTransform: 'none',
            fontSize: { xs: '1rem', md: '1.1rem' },
            borderRadius: 2,
            border: '2px solid #ffffff',
            '&:hover': {
              backgroundColor: primaryColor,
              color: '#ffffff'
            }
          }}
        >
          Start Line Production
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navigation onNavigateToAuth={onNavigateToAuth} />
      <HeroSection />
      <ScrollingTags />
      <FeaturesOverview />
      <ImageShowcase />
      <CTASection />
      <Footer />
    </Box>
  );
};

export default LineProductionLandingPage;